import React, { useState, useEffect } from "react";
import { useToastContext } from "@finitless/shared/contexts";
import { useUserStore } from "@finitless/shared/stores";
import {
  useProfilePage,
  usePhoneNumber,
  useSessions,
} from "@finitless/shared/hooks";
import { useTranslation } from "react-i18next";
import { DatePicker, PhoneInputSimple, Input, Modal } from "../ui";
import { Icon } from "../ui/UIElements/Icon";
import { formatDateByLocale } from "../../lib/localeFormatters";

const AccountPage = () => {
  const { t } = useTranslation(["profile", "account"]);
  const toast = useToastContext();
  const { user } = useUserStore();

  const {
    user: currentUser,
    updateProfile,
    isUpdating: isUpdatingProfile,
  } = useProfilePage();

  const {
    phoneNumber,
    error: phoneError,
    handlePhoneChange,
    getPhoneData,
    reset: resetPhone,
  } = usePhoneNumber();

  const {
    sessions,
    isLoading: sessionsLoading,
    revokeSession,
    revokeAllSessions,
    isRevokingSession,
    isRevokingAllSessions,
  } = useSessions();

  // Profile state
  const [isEditing, setIsEditing] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    lastname: "",
    birthdate: "",
    country_phone_code: "",
    cellphone: "",
    photo: null,
  });
  const [photoPreview, setPhotoPreview] = useState(null);

  // Password state
  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Sessions state
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showRevokeAllModal, setShowRevokeAllModal] = useState(false);
  const [sessionToRevoke, setSessionToRevoke] = useState(null);

  // Initialize form data when user data is available
  useEffect(() => {
    if (currentUser) {
      const initialData = {
        name: currentUser.name || "",
        lastname: currentUser.lastname || "",
        birthdate: currentUser.birthdate || "",
        country_phone_code: currentUser.country_phone_code || "",
        cellphone: currentUser.cellphone || "",
        photo: currentUser.photo || null,
      };
      setFormData(initialData);
      setOriginalData(initialData);
      setPhotoPreview(currentUser.photo || null);

      if (currentUser.country_phone_code && currentUser.cellphone) {
        const fullPhoneNumber = `+${currentUser.country_phone_code}${currentUser.cellphone}`;
        handlePhoneChange(fullPhoneNumber);
      } else {
        resetPhone();
      }
    }
  }, [currentUser]);

  // --- Profile handlers ---

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (profileErrors[name]) {
      setProfileErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error(t("toasts.invalidImage", { ns: "profile" }));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error(t("toasts.imageTooLarge", { ns: "profile" }));
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target.result;

        const previewUrl = URL.createObjectURL(file);
        setPhotoPreview(previewUrl);

        setFormData((prev) => ({
          ...prev,
          photo: base64String,
        }));

        if (profileErrors.photo) {
          setProfileErrors((prev) => ({
            ...prev,
            photo: "",
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    setFormData((prev) => ({
      ...prev,
      photo: "",
    }));
  };

  const getModifiedFields = () => {
    const modifiedFields = {};

    Object.keys(formData).forEach((key) => {
      if (key === "photo") {
        if (
          formData[key] &&
          typeof formData[key] === "string" &&
          formData[key].startsWith("data:image/")
        ) {
          modifiedFields[key] = formData[key];
        } else if (originalData[key] && !formData[key]) {
          modifiedFields[key] = "";
        }
      } else if (key === "country_phone_code" || key === "cellphone") {
        const phoneData = getPhoneData();
        const originalPhoneCode = originalData.country_phone_code || "";
        const originalCellphone = originalData.cellphone || "";

        if (
          phoneData.country_phone_code !== originalPhoneCode ||
          phoneData.cellphone !== originalCellphone
        ) {
          modifiedFields.country_phone_code = phoneData.country_phone_code;
          modifiedFields.cellphone = phoneData.cellphone;
        }
      } else {
        if (formData[key] !== originalData[key]) {
          modifiedFields[key] = formData[key];
        }
      }
    });

    return modifiedFields;
  };

  const isFieldModified = (fieldName) => {
    if (fieldName === "photo") {
      return (
        (formData[fieldName] &&
          typeof formData[fieldName] === "string" &&
          formData[fieldName].startsWith("data:image/")) ||
        (originalData[fieldName] && !formData[fieldName])
      );
    } else if (
      fieldName === "country_phone_code" ||
      fieldName === "cellphone"
    ) {
      const phoneData = getPhoneData();
      const originalPhoneCode = originalData.country_phone_code || "";
      const originalCellphone = originalData.cellphone || "";
      return (
        phoneData.country_phone_code !== originalPhoneCode ||
        phoneData.cellphone !== originalCellphone
      );
    }
    return formData[fieldName] !== originalData[fieldName];
  };

  const validateProfileForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t("validation.firstNameRequired", { ns: "profile" });
    }

    if (!formData.lastname.trim()) {
      newErrors.lastname = t("validation.lastNameRequired", { ns: "profile" });
    }

    if (phoneNumber && phoneError) {
      newErrors.phone = phoneError;
    }

    setProfileErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEditToggle = () => {
    if (isEditing) {
      setFormData(originalData);
      setPhotoPreview(originalData.photo || null);
      setProfileErrors({});

      if (originalData.country_phone_code && originalData.cellphone) {
        const fullPhoneNumber = `+${originalData.country_phone_code}${originalData.cellphone}`;
        handlePhoneChange(fullPhoneNumber);
      } else {
        resetPhone();
      }
    }
    setIsEditing(!isEditing);
  };

  const handleSaveProfile = async () => {
    if (!validateProfileForm()) {
      toast.error(t("toasts.fixErrorsBeforeSaving", { ns: "profile" }));
      return;
    }

    const modifiedFields = getModifiedFields();

    if (Object.keys(modifiedFields).length === 0) {
      toast.info(t("toasts.noChanges", { ns: "profile" }));
      setIsEditing(false);
      return;
    }

    try {
      await updateProfile({
        ...modifiedFields,
        userId: currentUser?.id,
      });

      toast.success(t("toasts.updated", { ns: "profile" }));
      setIsEditing(false);
      setProfileErrors({});

      const updatedOriginalData = { ...originalData, ...modifiedFields };
      setOriginalData(updatedOriginalData);

      if (modifiedFields.photo) {
        if (
          typeof modifiedFields.photo === "string" &&
          modifiedFields.photo.startsWith("data:image/")
        ) {
          setPhotoPreview(modifiedFields.photo);
        } else {
          setPhotoPreview(null);
        }
      }

      if (modifiedFields.country_phone_code || modifiedFields.cellphone) {
        setFormData((prev) => ({
          ...prev,
          country_phone_code:
            modifiedFields.country_phone_code || prev.country_phone_code,
          cellphone: modifiedFields.cellphone || prev.cellphone,
        }));
      }
    } catch (error) {
      console.error("Profile update error:", error);
      toast.error(error.message || t("toasts.updateFailed", { ns: "profile" }));
    }
  };

  const getInitials = (name, lastname) => {
    const firstInitial = name ? name.charAt(0).toUpperCase() : "";
    const lastInitial = lastname ? lastname.charAt(0).toUpperCase() : "";
    return firstInitial + lastInitial;
  };

  const getPhotoUrl = () => {
    if (photoPreview) {
      return photoPreview;
    }
    if (currentUser?.photo) {
      return currentUser.photo;
    }
    return null;
  };

  // --- Password handlers ---

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (passwordErrors[name]) {
      setPasswordErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordData.newPassword.trim()) {
      newErrors.newPassword = t("validation.newPasswordRequired", {
        ns: "account",
      });
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = t("validation.passwordMinLength", {
        ns: "account",
      });
    }

    if (!passwordData.confirmPassword.trim()) {
      newErrors.confirmPassword = t("validation.confirmPasswordRequired", {
        ns: "account",
      });
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = t("validation.passwordsDoNotMatch", {
        ns: "account",
      });
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChangePassword = async () => {
    if (!validatePasswordForm()) {
      toast.error(t("toasts.fixErrorsBeforeChangePassword", { ns: "account" }));
      return;
    }

    try {
      await updateProfile({
        userId: user.id,
        password: passwordData.newPassword,
      });

      toast.success(t("toasts.passwordChanged", { ns: "account" }));
      setPasswordData({
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordErrors({});
    } catch (error) {
      console.error("Password change error:", error);
      toast.error(
        error.message || t("toasts.passwordChangeFailed", { ns: "account" }),
      );
    }
  };

  // --- Sessions handlers ---

  const handleRevokeSessionClick = (session) => {
    setSessionToRevoke(session);
    setShowRevokeModal(true);
  };

  const handleRevokeAllSessionsClick = () => {
    setShowRevokeAllModal(true);
  };

  const confirmRevokeSession = async () => {
    try {
      await revokeSession(sessionToRevoke.id);
      toast.success(t("toasts.sessionRevoked", { ns: "account" }));
      setShowRevokeModal(false);
      setSessionToRevoke(null);
    } catch (error) {
      console.error("Session revoke error:", error);
      toast.error(t("toasts.revokeSessionFailed", { ns: "account" }));
    }
  };

  const confirmRevokeAllSessions = async () => {
    try {
      await revokeAllSessions();
      toast.success(t("toasts.allSessionsRevoked", { ns: "account" }));
      setShowRevokeAllModal(false);
    } catch (error) {
      console.error("Revoke all sessions error:", error);
      toast.error(t("toasts.revokeAllSessionsFailed", { ns: "account" }));
    }
  };

  const formatSessionDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      return t("session.justNow", { ns: "account" });
    } else if (diffInHours < 24) {
      return diffInHours === 1
        ? t("session.hourAgo", { count: diffInHours, ns: "account" })
        : t("session.hoursAgo", { count: diffInHours, ns: "account" });
    } else {
      return formatDateByLocale(date);
    }
  };

  const formatExpirationDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((date - now) / (1000 * 60 * 60 * 24));

    if (diffInDays < 0) {
      return t("session.expired", { ns: "account" });
    } else if (diffInDays === 0) {
      return t("session.expiresToday", { ns: "account" });
    } else if (diffInDays === 1) {
      return t("session.expiresTomorrow", { ns: "account" });
    } else if (diffInDays < 7) {
      return t("session.expiresInDays", { count: diffInDays, ns: "account" });
    } else {
      return t("session.expiresOn", {
        date: formatDateByLocale(date),
        ns: "account",
      });
    }
  };

  const getDeviceIcon = () => {
    return "Monitor";
  };

  const getDeviceName = (session) => {
    if (session.device) {
      return session.device;
    }
    if (session.current) {
      return t("session.currentDevice", { ns: "account" });
    }
    return t("session.unknownDevice", { ns: "account" });
  };

  const getDeviceIconColor = (session) => {
    if (session.current) {
      return "bg-green-500";
    }
    return "bg-blue-500";
  };

  // Show loading state only if user data is not available
  if (!currentUser) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500"></div>
          <p className="text-muted-foreground">
            {t("loading", { ns: "profile" })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen">
      {/* Header */}
      <div className="bg-background">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-foreground text-xl font-medium sm:text-2xl">
              {t("header.unifiedTitle", { ns: "profile" })}
            </h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">
              {t("header.unifiedSubtitle", { ns: "profile" })}
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            {isEditing ? (
              <>
                <button
                  onClick={handleEditToggle}
                  disabled={isUpdatingProfile}
                  className="text-foreground bg-background border-border hover:bg-accent w-full rounded-lg border px-3 py-2 text-xs font-normal transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-4 sm:text-sm"
                >
                  {t("actions.cancel", { ns: "profile" })}
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={isUpdatingProfile}
                  className="flex w-full items-center justify-center space-x-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 px-3 py-2 text-xs font-normal text-white transition-all hover:from-blue-600 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-4 sm:text-sm"
                >
                  {isUpdatingProfile ? (
                    <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-white sm:h-4 sm:w-4"></div>
                  ) : null}
                  <span>
                    {isUpdatingProfile
                      ? t("actions.saving", { ns: "profile" })
                      : Object.keys(getModifiedFields()).length > 0
                        ? t("actions.saveChangesWithCount", {
                            count: Object.keys(getModifiedFields()).length,
                            ns: "profile",
                          })
                        : t("actions.saveChanges", { ns: "profile" })}
                  </span>
                </button>
              </>
            ) : (
              <button
                onClick={handleEditToggle}
                className="w-full rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 px-3 py-2 text-xs font-normal text-white transition-all hover:from-blue-600 hover:to-purple-700 sm:w-auto sm:px-4 sm:text-sm"
              >
                {t("actions.editProfile", { ns: "profile" })}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-4 sm:py-6">
        <div className="space-y-4 sm:space-y-6">
          {/* Profile Section */}
          <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3">
            {/* Profile Summary */}
            <div className="lg:col-span-1">
              <div className="bg-card border-border rounded-lg border p-4 sm:p-6">
                <div className="text-center">
                  <div className="relative mx-auto mb-3 h-16 w-16 sm:mb-4 sm:h-20 sm:w-20">
                    {getPhotoUrl() ? (
                      <div className="relative">
                        <img
                          src={getPhotoUrl()}
                          alt={t("photo.alt", { ns: "profile" })}
                          className="border-border h-16 w-16 rounded-full border-2 object-cover sm:h-20 sm:w-20"
                        />
                        {isEditing && (
                          <>
                            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
                              <div className="flex items-center space-x-1">
                                <Icon
                                  name="Image"
                                  className="h-3 w-3 text-white sm:h-4 sm:w-4"
                                />
                                <Icon
                                  name="MoreVertical"
                                  className="h-3 w-3 text-white sm:h-4 sm:w-4"
                                />
                              </div>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handlePhotoChange}
                              className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                            />
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-pink-500 sm:h-20 sm:w-20">
                        <span className="text-lg font-semibold text-white sm:text-2xl">
                          {getInitials(formData.name, formData.lastname)}
                        </span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-card-foreground text-lg font-medium sm:text-xl">
                    {formData.name} {formData.lastname}
                  </h3>
                  <p className="text-muted-foreground mt-1 text-xs sm:text-sm">
                    {currentUser?.role ||
                      t("values.defaultRole", { ns: "profile" })}
                  </p>
                  <p className="text-muted-foreground mt-1 text-xs break-words sm:text-sm">
                    {currentUser?.email}
                  </p>
                  {isEditing && (
                    <div className="mt-3 space-y-2">
                      {getPhotoUrl() && (
                        <button
                          onClick={removePhoto}
                          className="text-xs text-red-500 transition-colors hover:text-red-600 sm:text-sm"
                        >
                          {t("photo.remove", { ns: "profile" })}
                        </button>
                      )}
                      {isFieldModified("photo") && (
                        <span className="text-xs font-normal text-blue-500">
                          {t("labels.modified", { ns: "profile" })}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Personal Information */}
            <div className="lg:col-span-2">
              <div className="bg-card border-border rounded-lg border p-4 sm:p-6">
                <h3 className="text-card-foreground mb-4 text-base font-medium sm:mb-6 sm:text-lg">
                  {t("sections.personalInformation", { ns: "profile" })}
                </h3>

                <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
                  {/* First Name */}
                  <div>
                    {isEditing ? (
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder={t("placeholders.firstName", {
                          ns: "profile",
                        })}
                        label={
                          <span>
                            {t("labels.firstName", { ns: "profile" })}
                            {isFieldModified("name") && (
                              <span className="ml-2 text-xs font-normal text-blue-500">
                                {t("labels.modified", { ns: "profile" })}
                              </span>
                            )}
                          </span>
                        }
                        error={profileErrors.name}
                        className={
                          isFieldModified("name") ? "border-blue-500" : ""
                        }
                      />
                    ) : (
                      <div>
                        <label className="text-muted-foreground mb-2 block text-xs font-normal sm:text-sm">
                          {t("labels.firstName", { ns: "profile" })}
                        </label>
                        <div className="text-card-foreground text-sm sm:text-base">
                          {formData.name ||
                            t("values.notProvided", { ns: "profile" })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Last Name */}
                  <div>
                    {isEditing ? (
                      <Input
                        type="text"
                        name="lastname"
                        value={formData.lastname}
                        onChange={handleInputChange}
                        placeholder={t("placeholders.lastName", {
                          ns: "profile",
                        })}
                        label={
                          <span>
                            {t("labels.lastName", { ns: "profile" })}
                            {isFieldModified("lastname") && (
                              <span className="ml-2 text-xs font-normal text-blue-500">
                                {t("labels.modified", { ns: "profile" })}
                              </span>
                            )}
                          </span>
                        }
                        error={profileErrors.lastname}
                        className={
                          isFieldModified("lastname") ? "border-blue-500" : ""
                        }
                      />
                    ) : (
                      <div>
                        <label className="text-muted-foreground mb-2 block text-xs font-normal sm:text-sm">
                          {t("labels.lastName", { ns: "profile" })}
                        </label>
                        <div className="text-card-foreground text-sm sm:text-base">
                          {formData.lastname ||
                            t("values.notProvided", { ns: "profile" })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Birth Date */}
                  <div>
                    <label className="text-muted-foreground mb-2 block text-xs font-normal sm:text-sm">
                      {t("labels.birthDate", { ns: "profile" })}
                      {isEditing && isFieldModified("birthdate") && (
                        <span className="ml-2 text-xs font-normal text-blue-500">
                          {t("labels.modified", { ns: "profile" })}
                        </span>
                      )}
                    </label>
                    {isEditing ? (
                      <DatePicker
                        value={
                          formData.birthdate
                            ? new Date(formData.birthdate)
                            : undefined
                        }
                        onChange={(date) => {
                          const formattedDate = date
                            ? date.toISOString().split("T")[0]
                            : "";
                          setFormData((prev) => ({
                            ...prev,
                            birthdate: formattedDate,
                          }));
                        }}
                        placeholder={t("placeholders.birthDate", {
                          ns: "profile",
                        })}
                        error={profileErrors.birthdate}
                        className={
                          isFieldModified("birthdate") ? "border-blue-500" : ""
                        }
                      />
                    ) : (
                      <div className="text-card-foreground text-sm sm:text-base">
                        {formData.birthdate
                          ? formatDateByLocale(formData.birthdate)
                          : t("values.notProvided", { ns: "profile" })}
                      </div>
                    )}
                    {profileErrors.birthdate && (
                      <p className="mt-1 text-xs text-red-500 sm:text-sm">
                        {profileErrors.birthdate}
                      </p>
                    )}
                  </div>

                  {/* Phone Number */}
                  <div className="md:col-span-2">
                    {isEditing ? (
                      <PhoneInputSimple
                        label={
                          <span>
                            {t("labels.phoneNumber", { ns: "profile" })}
                            {(isFieldModified("country_phone_code") ||
                              isFieldModified("cellphone")) && (
                              <span className="ml-2 text-xs font-normal text-blue-500">
                                {t("labels.modified", { ns: "profile" })}
                              </span>
                            )}
                          </span>
                        }
                        value={phoneNumber}
                        onChange={handlePhoneChange}
                        placeholder={t("placeholders.phoneNumber", {
                          ns: "profile",
                        })}
                        disabled={false}
                        error={profileErrors.phone}
                        size="md"
                      />
                    ) : (
                      <div>
                        <label className="text-muted-foreground mb-2 block text-xs font-normal sm:text-sm">
                          {t("labels.phoneNumber", { ns: "profile" })}
                        </label>
                        <div className="text-card-foreground text-sm sm:text-base">
                          {phoneNumber ||
                            t("values.notProvided", { ns: "profile" })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Password & Authentication Card */}
          <div className="bg-card border-border rounded-lg border p-4 sm:p-6">
            <h3 className="text-card-foreground mb-4 text-base font-medium sm:mb-6 sm:text-lg">
              {t("sections.passwordAuthentication", { ns: "account" })}
            </h3>

            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-card-foreground font-medium">
                    {t("labels.password", { ns: "account" })}
                  </div>
                </div>
                <button
                  onClick={() => setIsChangingPassword(!isChangingPassword)}
                  className="px-4 py-2 text-sm font-medium text-blue-500 transition-colors hover:text-blue-600"
                >
                  {t("actions.changePassword", { ns: "account" })}
                </button>
              </div>

              {isChangingPassword && (
                <div className="border-border space-y-4 border-t pt-6">
                  <Input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    placeholder={t("placeholders.newPassword", {
                      ns: "account",
                    })}
                    label={
                      <span>
                        {t("labels.newPasswordHint", { ns: "account" })}
                      </span>
                    }
                    error={passwordErrors.newPassword}
                  />

                  <Input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder={t("placeholders.confirmNewPassword", {
                      ns: "account",
                    })}
                    label={t("labels.confirmNewPassword", { ns: "account" })}
                    error={passwordErrors.confirmPassword}
                  />

                  <div className="flex space-x-3">
                    <button
                      onClick={handleChangePassword}
                      disabled={isUpdatingProfile}
                      className="flex items-center space-x-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 px-4 py-2 text-sm font-medium text-white transition-all hover:from-blue-600 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isUpdatingProfile ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                      ) : null}
                      <span>
                        {isUpdatingProfile
                          ? t("actions.changing", { ns: "account" })
                          : t("actions.updatePassword", { ns: "account" })}
                      </span>
                    </button>

                    <button
                      onClick={() => {
                        setIsChangingPassword(false);
                        setPasswordData({
                          newPassword: "",
                          confirmPassword: "",
                        });
                        setPasswordErrors({});
                      }}
                      className="text-card-foreground bg-card border-border hover:bg-accent rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
                    >
                      {t("actions.cancel", { ns: "account" })}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Active Sessions Card */}
          <div className="bg-card border-border rounded-lg border p-4 sm:p-6">
            <div className="mb-4 flex items-center justify-between sm:mb-6">
              <h3 className="text-card-foreground text-base font-medium sm:text-lg">
                {t("sections.activeSessions", { ns: "account" })}
              </h3>
              {sessions && sessions.length > 1 && (
                <button
                  onClick={handleRevokeAllSessionsClick}
                  disabled={isRevokingAllSessions}
                  className="flex cursor-pointer items-center space-x-1 px-3 py-1.5 text-sm font-medium text-red-400 transition-colors hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isRevokingAllSessions ? (
                    <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-red-400"></div>
                  ) : null}
                  <span>
                    {isRevokingAllSessions
                      ? t("actions.revoking", { ns: "account" })
                      : t("actions.revokeAll", { ns: "account" })}
                  </span>
                </button>
              )}
            </div>

            {sessionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-500"></div>
                <span className="text-muted-foreground ml-2">
                  {t("loading.sessions", { ns: "account" })}
                </span>
              </div>
            ) : sessions && sessions.length > 0 ? (
              <div className="space-y-3">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className={`flex items-center justify-between rounded-lg border p-4 ${
                      session.current
                        ? "border-green-500/30 bg-gradient-to-r from-green-500/10 to-blue-500/10 shadow-lg shadow-green-500/10"
                        : "border-border"
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className={`h-10 w-10 rounded-lg ${getDeviceIconColor(session)} flex items-center justify-center ${
                          session.current
                            ? "ring-opacity-50 shadow-lg ring-2 ring-green-400"
                            : ""
                        }`}
                      >
                        <Icon
                          name={getDeviceIcon()}
                          className="h-5 w-5 text-white"
                        />
                      </div>
                      <div>
                        <div
                          className={`font-medium ${
                            session.current
                              ? "text-green-400"
                              : "text-card-foreground"
                          }`}
                        >
                          {getDeviceName(session)}
                        </div>
                        <div
                          className={`text-sm ${
                            session.current
                              ? "text-green-300"
                              : "text-muted-foreground"
                          }`}
                        >
                          {session.current
                            ? t("session.currentSession", { ns: "account" })
                            : formatSessionDate(session.created_at)}{" "}
                          • {formatExpirationDate(session.valid_thru)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {session.current ? (
                        <span className="rounded-full border border-green-500/30 bg-green-500/20 px-2 py-1 text-xs font-medium text-green-400">
                          {t("labels.active", { ns: "account" })}
                        </span>
                      ) : (
                        <button
                          onClick={() => handleRevokeSessionClick(session)}
                          disabled={isRevokingSession}
                          className="flex cursor-pointer items-center space-x-1 text-sm font-medium text-red-400 transition-colors hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isRevokingSession ? (
                            <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-red-400"></div>
                          ) : null}
                          <span>
                            {isRevokingSession
                              ? t("actions.revoking", { ns: "account" })
                              : t("actions.revoke", { ns: "account" })}
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <div className="text-muted-foreground">
                  {t("empty.noActiveSessions", { ns: "account" })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Revoke Session Confirmation Modal */}
      <Modal
        isOpen={showRevokeModal}
        onClose={() => {
          setShowRevokeModal(false);
          setSessionToRevoke(null);
        }}
        title={t("modals.revokeSession.title", { ns: "account" })}
        subtitle={t("modals.revokeSession.subtitle", { ns: "account" })}
        maxWidth="max-w-md"
      >
        {sessionToRevoke && (
          <div className="space-y-4">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
              <div className="flex items-center space-x-3">
                <Icon name="AlertTriangle" className="h-5 w-5 text-red-500" />
                <div>
                  <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                    {getDeviceName(sessionToRevoke)}
                  </h4>
                  <p className="mt-1 text-xs text-red-600 dark:text-red-300">
                    {sessionToRevoke.current
                      ? t("session.currentSession", { ns: "account" })
                      : formatSessionDate(sessionToRevoke.created_at)}{" "}
                    • {formatExpirationDate(sessionToRevoke.valid_thru)}
                  </p>
                </div>
              </div>
            </div>

            <p className="text-muted-foreground text-sm">
              {t("modals.revokeSession.description", { ns: "account" })}
            </p>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={confirmRevokeSession}
                disabled={isRevokingSession}
                className="flex flex-1 items-center justify-center space-x-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isRevokingSession ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                ) : null}
                <span>
                  {isRevokingSession
                    ? t("actions.revoking", { ns: "account" })
                    : t("actions.revokeSession", { ns: "account" })}
                </span>
              </button>
              <button
                onClick={() => {
                  setShowRevokeModal(false);
                  setSessionToRevoke(null);
                }}
                disabled={isRevokingSession}
                className="text-card-foreground bg-card border-border hover:bg-accent flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("actions.cancel", { ns: "account" })}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Revoke All Sessions Confirmation Modal */}
      <Modal
        isOpen={showRevokeAllModal}
        onClose={() => setShowRevokeAllModal(false)}
        title={t("modals.revokeAllSessions.title", { ns: "account" })}
        subtitle={t("modals.revokeAllSessions.subtitle", { ns: "account" })}
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
            <div className="flex items-center space-x-3">
              <Icon name="AlertTriangle" className="h-5 w-5 text-red-500" />
              <div>
                <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                  {t("modals.revokeAllSessions.warning", {
                    count: sessions?.length || 0,
                    ns: "account",
                  })}
                </h4>
                <p className="mt-1 text-xs text-red-600 dark:text-red-300">
                  {t("modals.revokeAllSessions.warningSubtext", {
                    ns: "account",
                  })}
                </p>
              </div>
            </div>
          </div>

          <p className="text-muted-foreground text-sm">
            {t("modals.revokeAllSessions.description", { ns: "account" })}
          </p>

          <div className="flex space-x-3 pt-4">
            <button
              onClick={confirmRevokeAllSessions}
              disabled={isRevokingAllSessions}
              className="flex flex-1 items-center justify-center space-x-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isRevokingAllSessions ? (
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
              ) : null}
              <span>
                {isRevokingAllSessions
                  ? t("actions.revokingAll", { ns: "account" })
                  : t("actions.revokeAllSessions", { ns: "account" })}
              </span>
            </button>
            <button
              onClick={() => setShowRevokeAllModal(false)}
              disabled={isRevokingAllSessions}
              className="text-card-foreground bg-card border-border hover:bg-accent flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("actions.cancel", { ns: "account" })}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AccountPage;
